import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  VerifyBeneficiaryStatus,
  type VerifyBeneficiaryResponse,
} from 'api/v2/transactions/verifyBeneficiary';
import { AlertV2, type AlertType } from 'components/atoms/AlertV2';
import classNames from 'classnames';
import {
  CheckCircleIconOutline,
  ExclamationCircleIconOutline,
  ExclamationIconOutline,
  InformationCircleIconOutline,
} from 'utils/icons';

interface BeneficiaryVerificationStatusProps {
  verificationData?: VerifyBeneficiaryResponse;
  onNameSuggestionClick?: (suggestedName: string) => void;
  className?: string;
  variant?: 'default' | 'alternative';
}

const BeneficiaryVerificationStatus: FC<BeneficiaryVerificationStatusProps> = ({
  verificationData,
  onNameSuggestionClick,
  className = '',
  variant = 'alternative',
}) => {
  const { t } = useTranslation();

  if (!verificationData) return null;

  const { status } = verificationData;

  const config = {
    [VerifyBeneficiaryStatus.Match]: {
      type: 'success' as AlertType,
      Icon: CheckCircleIconOutline,
      color: 'text-[#059669]',
      title: t('accountable_banking.beneficiary_verification.match.title'),
      description: t(
        'accountable_banking.beneficiary_verification.match.message',
      ),
    },
    [VerifyBeneficiaryStatus.CloseMatch]: {
      type: 'information' as AlertType,
      Icon: InformationCircleIconOutline,
      color: 'text-primary-600',
      title: '',
      description: (
        <span>
          {t(
            'accountable_banking.beneficiary_verification.close_match.message_prefix',
          )}
          <a
            className="underline cursor-pointer"
            onClick={() =>
              onNameSuggestionClick?.(verificationData?.nameSuggestion ?? '')
            }
          >
            {verificationData.nameSuggestion}
          </a>
          {t(
            'accountable_banking.beneficiary_verification.close_match.message_suffix',
          )}
        </span>
      ),
    },
    [VerifyBeneficiaryStatus.NoMatch]: {
      type: 'error' as AlertType,
      Icon: ExclamationCircleIconOutline,
      color: 'text-[#DC2626]',
      title: t('accountable_banking.beneficiary_verification.no_match.title'),
      description: t(
        'accountable_banking.beneficiary_verification.no_match.message',
      ),
    },
    [VerifyBeneficiaryStatus.VerificationNotPossible]: {
      type: 'warning' as AlertType,
      Icon: ExclamationIconOutline,
      color: 'text-[#D97706]',
      title: t(
        'accountable_banking.beneficiary_verification.not_possible.title',
      ),
      description: t(
        'accountable_banking.beneficiary_verification.not_possible.message',
      ),
    },
  };
  const currentConfig = config[status];

  if (!currentConfig) return null;

  const { Icon, color, type, title, description } = currentConfig;

  if (variant === 'alternative') {
    return (
      <div className={classNames('flex items-center gap-2 text-sm', className)}>
        <div className="self-start">
          <Icon className={classNames('w-5 h-5', color)} />
        </div>
        <span className={color}>
          {Boolean(title) && <strong>{title}: </strong>}
          {description}
        </span>
      </div>
    );
  }

  return (
    <AlertV2
      type={type}
      title={title}
      description={description}
      size="large"
      className={classNames('mt-3 mb-3 min-w-full', className)}
    />
  );
};

export default BeneficiaryVerificationStatus;
